//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import {
  createCommunityTag,
  createInviteLinks,
  editInviteLink,
  editVibeCommunity,
  exportCommunityMembersCsv,
  exportInviteLinks,
  getInviteLinks,
  getVibeCommunity,
  getVibeCommunityTags,
  inviteCommunityMembers,
  inviteCommunityMembersCsv,
  searchCommunityMembers,
  searchVibeCommunities,
  uploadVibeCommunityLogo,
} from '@apis/campaign-requests/sampling/vibe-communities/vibeCommunitiesApi';
import {
  EditVibeCommunityRequest,
  ExportCommunityMembersRequest,
  ExportInviteLinksRequest,
  InviteCommunityMembersRequest,
  InviteLinkStatus,
  SearchCommunityMembersRequest,
  VibeCommunityInviteLinksRequest,
  VibeCommunityTag,
} from '@apis/campaign-requests/sampling/vibe-communities/vibeCommunitiesApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import VibeCommunityId from '@models/VibeCommunityId';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

/**
 * Hook to search for all available Vibe communities.
 */
export function useSearchVibeCommunitiesQuery(enabled = true) {
  return useQuery({
    enabled,
    queryFn: ({ signal }) => searchVibeCommunities({ signal }),
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.VibeCommunities,
    ],
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

/**
 * Hook to fetch details of a specific Vibe community.
 */
export function useVibeCommunityQuery(
  communityId: VibeCommunityId | undefined,
) {
  return useQuery({
    enabled: isDefined(communityId),
    queryFn: ({ signal }) => {
      if (!isDefined(communityId)) {
        throw new Error(
          'Undefined communityId. communityId is required by useVibeCommunityQuery',
        );
      }

      return getVibeCommunity({ communityId }, { signal });
    },
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.VibeCommunities,
      communityId,
    ],
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

/**
 * Hook to fetch tags associated with a Vibe community.
 */
export function useVibeCommunityTagsQuery<TData = VibeCommunityTag[]>(
  communityId: VibeCommunityId | undefined,
  select?: (data: VibeCommunityTag[]) => TData,
) {
  return useQuery({
    enabled: isDefined(communityId),
    queryFn: ({ signal }) => {
      if (!isDefined(communityId)) {
        throw new Error(
          'Undefined communityId. communityId is required by useVibeCommunityTagsQuery',
        );
      }

      return getVibeCommunityTags({ communityId }, { signal }).then(
        (res) => res.results,
      );
    },
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.VibeCommunities,
      communityId,
      QueryKeyConstants.VibeCommunitiesTags,
    ],
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

/**
 * Hook to edit a Vibe Community's name, description, and logo.
 */
export function useEditVibeCommunityMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: EditVibeCommunityRequest) => {
      let logoUrl = req.logoUrl;

      if (isDefined(req.logoFile)) {
        try {
          const uploadResponse = await uploadVibeCommunityLogo({
            communityId: req.communityId,
            file: req.logoFile,
          });
          logoUrl = uploadResponse.logoUrl;
        } catch (error) {
          throw new Error(
            'Failed to upload community logo. Please try again.',
            { cause: error },
          );
        }
      }

      try {
        return await editVibeCommunity({
          communityId: req.communityId,
          description: req.description,
          logoUrl,
          name: req.name,
        });
      } catch (error) {
        throw new Error(
          'Failed to update community details. Please try again.',
          { cause: error },
        );
      }
    },
    onSettled: async (_data, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          exact: true,
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CampaignsSampling,
            QueryKeyConstants.VibeCommunities,
          ],
        }),
        queryClient.invalidateQueries({
          exact: true,
          queryKey: [
            QueryKeyConstants.Campaigns,
            QueryKeyConstants.CampaignsSampling,
            QueryKeyConstants.VibeCommunities,
            variables.communityId,
          ],
        }),
      ]);
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          message: 'We have updated your Ambassador community configuration',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Community Updated',
          type: ToastType.SUCCESS,
        }),
      );
    },
  });
}

/**
 * Hook to search for members within a Vibe community.
 */
export function useSearchCommunityMembersQuery(
  req: SearchCommunityMembersRequest | undefined,
) {
  return useQuery({
    enabled: isDefined(req),
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => {
      if (!isDefined(req)) {
        throw new Error(
          'Undefined req. req is required by useSearchCommunityMembersQuery',
        );
      }

      return searchCommunityMembers(req, { signal });
    },
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.VibeCommunities,
      req?.communityId,
      QueryKeyConstants.VibeCommunitiesMembers,
      req,
    ],
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

/**
 * Hook to send community invites to a list of emails entered manually by the user.
 */
export function useSendCommunityInvitesMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: InviteCommunityMembersRequest) =>
      inviteCommunityMembers({
        communityId: req.communityId,
        emails: req.emails,
        ...(isDefined(req.tagIds) && { tagIds: req.tagIds }),
      }),
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CampaignsSampling,
          QueryKeyConstants.VibeCommunities,
          variables.communityId,
          QueryKeyConstants.VibeCommunitiesMembers,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          message: 'Invites have been sent to the provided email addresses.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Invites Sent',
          type: ToastType.SUCCESS,
        }),
      );
    },
  });
}

/**
 * Hook to send community invites to a list of emails provided via CSV file upload.
 */
export function useSendCommunityInvitesCsvMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteCommunityMembersCsv,
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CampaignsSampling,
          QueryKeyConstants.VibeCommunities,
          variables.communityId,
          QueryKeyConstants.VibeCommunitiesMembers,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          message: 'Invites have been sent to all members in your CSV file.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Invites Sent',
          type: ToastType.SUCCESS,
        }),
      );
    },
  });
}

export function useExportCommunityMembersCsvMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (req: ExportCommunityMembersRequest) =>
      exportCommunityMembersCsv(req),
    onError: (error) => {
      const errorMessage =
        getRequestErrorMessage(error) ??
        'Failed to export CSV. Please try again.';
      dispatch(
        toastNotificationsActions.addToastNotification({
          message: errorMessage,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Export Failed',
          type: ToastType.ERROR,
        }),
      );
    },
  });
}

/**
 * Hook to export invite links as a CSV file.
 */
export function useExportInviteLinksMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (req: ExportInviteLinksRequest) => exportInviteLinks(req),
    onError: (error) => {
      const errorMessage =
        getRequestErrorMessage(error) ??
        'Failed to export CSV. Please try again.';
      dispatch(
        toastNotificationsActions.addToastNotification({
          message: errorMessage,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Export Failed',
          type: ToastType.ERROR,
        }),
      );
    },
  });
}

/**
 * Hook to create a new tag for a Vibe community.
 */
export function useCreateCommunityTagMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCommunityTag,
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        exact: true,
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CampaignsSampling,
          QueryKeyConstants.VibeCommunities,
          variables.communityId,
          QueryKeyConstants.VibeCommunitiesTags,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          message: 'Tag has been successfully created.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Tag Created',
          type: ToastType.SUCCESS,
        }),
      );
    },
  });
}

/**
 * Hook to fetch invite links for a Vibe community.
 */
export function useInviteLinksQuery(
  req: Omit<VibeCommunityInviteLinksRequest, 'communityId'> & {
    communityId?: VibeCommunityId;
  },
) {
  return useQuery({
    enabled: isDefined(req.communityId),
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => {
      if (!isDefined(req.communityId)) {
        throw new Error(
          'Undefined communityId. communityId is required by useInviteLinksQuery',
        );
      }

      return getInviteLinks(
        { ...req, communityId: req.communityId },
        { signal },
      );
    },
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.VibeCommunities,
      req.communityId,
      QueryKeyConstants.VibeCommunitiesInviteLinks,
      req,
    ],
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

/**
 * Hook to create invite links for a Vibe community.
 */
export function useCreateInviteLinksMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createInviteLinks,
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CampaignsSampling,
          QueryKeyConstants.VibeCommunities,
          variables.communityId,
          QueryKeyConstants.VibeCommunitiesInviteLinks,
        ],
      });
    },
  });
}

/**
 * Hook to edit an invite link for a Vibe community.
 */
export function useEditInviteLinkMutation(
  communityId: VibeCommunityId | undefined,
) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      req: Omit<Parameters<typeof editInviteLink>[0], 'communityId'>,
    ) => {
      if (isUndefined(communityId)) {
        throw new Error(
          'Undefined communityId. communityId is required by useEditInviteLinkMutation',
        );
      }
      return editInviteLink({ ...req, communityId });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CampaignsSampling,
          QueryKeyConstants.VibeCommunities,
          communityId,
          QueryKeyConstants.VibeCommunitiesInviteLinks,
        ],
      });
    },
    onSuccess: (_data, variables) => {
      const isRevoked = variables.status === InviteLinkStatus.WITHDRAWN;
      dispatch(
        toastNotificationsActions.addToastNotification({
          message: isRevoked
            ? 'Invite link has been successfully revoked.'
            : 'Invite link has been successfully updated.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: isRevoked ? 'Invite Link Revoked' : 'Invite Link Updated',
          type: ToastType.SUCCESS,
        }),
      );
    },
  });
}
